// Beállítások → Jelentések (2.7b): the sentences and checks of the recipient
// list, apart from the page so they run on Node in the admin tests
// (tests/admin/test_digest_recipients_model.py). The API is the authority on
// what is stored; this says it in Hungarian and catches the obvious before a
// round trip.

import type {
  DigestFrequency,
  DigestLetter,
  DigestLetterSpec,
  DigestRecipient,
  DigestRecipientEvent,
  DigestRecipients,
} from "@/lib/api";

export const WEEKDAYS = ["hétfő", "kedd", "szerda", "csütörtök", "péntek", "szombat", "vasárnap"];
/** "on Monday": the day with its ending, for "hetente, hétfőn". */
const ON_DAY = ["hétfőn", "kedden", "szerdán", "csütörtökön", "pénteken", "szombaton", "vasárnap"];

export const LETTER_WORD: Record<DigestLetter, string> = {
  owner: "Tulajdonosi",
  management: "Vezetőségi",
};

export const FREQUENCY_WORD: Record<DigestFrequency, string> = {
  daily: "naponta",
  weekly: "hetente",
};

/** "naponta", "hetente, hétfőn". */
export function frequencyLine(row: Pick<DigestRecipient, "frequency" | "weekday">): string {
  if (row.frequency === "weekly") {
    const day = row.weekday === null ? null : ON_DAY[row.weekday];
    return day ? `hetente, ${day}` : "hetente";
  }
  return "naponta";
}

/** "minden szakasz", or which ones are switched off. */
export function sectionsLine(row: Pick<DigestRecipient, "letter" | "sections">, letters: DigestLetterSpec[]): string {
  const spec = letters.find((one) => one.letter === row.letter);
  if (spec === undefined) return "";
  const off = spec.sections.filter((section) => row.sections[section.key] === false).map((s) => s.label);
  if (off.length === 0) return "minden szakasz";
  if (off.length === spec.sections.length) return "csak a fejléc";
  return `kikapcsolva: ${off.join(", ")}`;
}

/** What happens while nobody is on the list. */
export function fallbackLine(fallback: DigestRecipients["fallback"]): string | null {
  if (!fallback.active) return null;
  const who = fallback.source === "EMC_ALERT_EMAIL_TO" ? "az EMC_ALERT_EMAIL_TO címei" : "minden aktív admin";
  if (fallback.emails.length === 0) {
    return `A lista üres, és nincs kinek küldeni (${who}): reggeli levél most senkinek nem megy.`;
  }
  return `A lista üres, ezért a tulajdonosi levelet naponta ${who} kapják: ${fallback.emails.join(", ")}. Az első hozzáadott címmel ez megszűnik.`;
}

export interface RecipientForm {
  email: string;
  letter: DigestLetter;
  frequency: DigestFrequency;
  weekday: number;
  sections: Record<string, boolean>;
  is_active: boolean;
}

export function blankForm(letters: DigestLetterSpec[]): RecipientForm {
  return {
    email: "",
    letter: "owner",
    frequency: "daily",
    weekday: 0,
    sections: allOn(letters, "owner"),
    is_active: true,
  };
}

export function formOf(row: DigestRecipient): RecipientForm {
  return {
    email: row.email,
    letter: row.letter,
    frequency: row.frequency,
    weekday: row.weekday ?? 0,
    sections: { ...row.sections },
    is_active: row.is_active,
  };
}

/** Every section of a letter, switched on. */
export function allOn(letters: DigestLetterSpec[], letter: DigestLetter): Record<string, boolean> {
  const spec = letters.find((one) => one.letter === letter);
  return Object.fromEntries((spec?.sections ?? []).map((section) => [section.key, true]));
}

/** The form as the API takes it: a weekday only for a weekly letter. */
export function valuesOf(form: RecipientForm) {
  return {
    email: form.email.trim().toLowerCase(),
    letter: form.letter,
    frequency: form.frequency,
    weekday: form.frequency === "weekly" ? form.weekday : null,
    sections: form.sections,
    is_active: form.is_active,
  };
}

const EMAIL = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;

/** The sentence under the field that is wrong, before the API is asked. */
export function checkForm(form: RecipientForm, rows: DigestRecipient[], editing: number | null): Record<string, string> {
  const errors: Record<string, string> = {};
  const email = form.email.trim().toLowerCase();
  if (!EMAIL.test(email)) errors.email = "Egyetlen, puszta email-cím kell (név és <> nélkül).";
  else if (rows.some((row) => row.id !== editing && row.email === email && row.letter === form.letter)) {
    errors.email = "Ez a cím már kapja ezt a levelet.";
  }
  if (form.frequency === "weekly" && !(form.weekday >= 0 && form.weekday <= 6)) {
    errors.weekday = "Heti levélhez a hét napja is kell.";
  }
  return errors;
}

/** One change of the list, as a line: who did what to which address. */
export function eventLine(event: DigestRecipientEvent): string {
  if (event.action === "recipient.added") return `${event.actor} hozzáadta: ${event.email}`;
  if (event.action === "recipient.removed") return `${event.actor} törölte: ${event.email}`;
  const changes = (event.detail.changes ?? {}) as Record<string, unknown>;
  const what = Object.keys(changes)
    .map((name) => CHANGE_WORD[name] ?? name)
    .join(", ");
  return `${event.actor} módosította: ${event.email}${what ? ` (${what})` : ""}`;
}

const CHANGE_WORD: Record<string, string> = {
  email: "cím",
  letter: "levél",
  frequency: "gyakoriság",
  weekday: "nap",
  sections: "szakaszok",
  is_active: "be/ki",
};
